import type { FC } from 'react';
import { Button } from '@george-labs.com/design-system';

import type { AdditionalData } from '@shared/products-types';
import { getDefaultTitle, getTitleAlignedWithGeorgeApp } from '@shared/products-general-service';
import {
    AliasChangeDropdown,
    PersonalizationCardOverviewAppearanceChange,
    ProductColorChangeDropdown,
} from '@retail/products-customisation';
import { useActiveCustomizationFeature } from '@shared/products-core';
import { t } from '@luna/i18n';

import { isAliasChangeAllowed } from '../services/aliasChangeService';
import { updateGAPIAccountById } from '../gAPIAccountEntitySlice';
import { useProductData } from '../hooks/useProductData';

type Props = {
    productId: string;
};
export const PersonalizationCardCustomizations: FC<Props> = ({ productId }) => {
    const { activateAliasChangeCustomization, activateColorChangeCustomization } =
        useActiveCustomizationFeature();
    const { product: account, updateAlias } = useProductData(productId);

    if (!account) {
        throw Error('No account is present');
    }

    const handleColorChangeSave = async (color: string) => {
        await updateGAPIAccountById({
            id: account.id,
            changes: {
                id: account.id,
                additionalData: {
                    ...account.additionalData,
                    accountColor: {
                        ...account.additionalData?.accountColor,
                        name: color.toUpperCase(),
                    },
                },
            },
        });
    };

    const handleOverviewAppearanceChange = async (newOverviewState: boolean) => {
        await updateGAPIAccountById({
            id: account.id,
            changes: {
                id: account.id,
                additionalData: {
                    hidden: newOverviewState,
                } as unknown as AdditionalData,
            },
        });
    };

    const hasAlias = Boolean(account.alias);

    return (
        <>
            {isAliasChangeAllowed(account) && (
                <AliasChangeDropdown
                    alias={getTitleAlignedWithGeorgeApp(account)}
                    hasAlias={hasAlias}
                    aliasChangeContext="details"
                    onAliasSave={updateAlias}
                    productType={account.type}
                    toggle={
                        <Button
                            icon={Button.ICON.EDIT}
                            size={Button.SIZE.SMALL}
                            onClick={activateAliasChangeCustomization}
                            data-cy="product-settings-edit-alias-button"
                            className="g-inverted"
                        >
                            {t('mainOverview.action.changeName_a11y')}
                        </Button>
                    }
                    placeholder={getDefaultTitle(account)}
                    trigger="productDetail"
                />
            )}
            <ProductColorChangeDropdown
                data-cy="products-color-change-dropdown"
                toggle={
                    <Button
                        icon={Button.ICON.PALETTE}
                        size={Button.SIZE.SMALL}
                        onClick={activateColorChangeCustomization}
                        data-cy="product-edit-color-button"
                        className="g-inverted"
                    >
                        {t('mainOverview.action.changeColor_a11y')}
                    </Button>
                }
                actionButtonDataCy="product-edit-color-save-button"
                onColorSave={handleColorChangeSave}
                colorChangeContext="details"
                trigger="productDetail"
                accountType={account.type}
            />
            <PersonalizationCardOverviewAppearanceChange
                onChange={handleOverviewAppearanceChange}
                hidden={account?.additionalData?.hidden}
                productType={account.type}
            />
        </>
    );
};
